import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, Theme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Get saved preference from localStorage or default to 'dark'
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('themeMode') as ThemeMode;
    return savedMode || 'dark';
  });

  // Save preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: '#667eea',
            light: '#8b9eff',
            dark: '#4c5fd7',
          },
          secondary: {
            main: '#f093fb',
            light: '#ffb3ff',
            dark: '#d66fe8',
          },
          background: {
            default: mode === 'light' ? '#f8fafc' : '#0f172a',
            paper: mode === 'light' ? '#ffffff' : '#1e293b',
          },
          text: {
            primary: mode === 'light' ? '#0f172a' : '#f1f5f9',
            secondary: mode === 'light' ? '#475569' : '#94a3b8',
          },
          divider: mode === 'light' ? '#e2e8f0' : '#334155',
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        },
         zIndex: { tooltip: 2100 },
        components: {
          MuiAppBar: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#2d3e50',
                color: mode === 'light' ? '#0f172a' : '#f1f5f9',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
                borderColor: mode === 'light' ? '#e2e8f0' : '#334155',
              },
            },
          },
          MuiDialog: {
            styleOverrides: {
              paper: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
                color: mode === 'light' ? '#0f172a' : '#f1f5f9',
                backgroundImage: 'none',
              },
            },
          },
          MuiDialogTitle: {
            styleOverrides: {
              root: {
                background: mode === 'light' 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(135deg, #4c5fd7 0%, #5b3a8f 100%)',
                color: '#ffffff',
                borderBottom: 'none',
              },
            },
          },
          MuiDialogContent: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
                color: mode === 'light' ? '#0f172a' : '#f1f5f9',
              },
            },
          },
          MuiDialogActions: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#f8fafc' : '#1e293b',
                borderTop: mode === 'light' ? '1px solid #e2e8f0' : '1px solid #334155',
                padding: '16px 24px',
              },
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiInputBase-root': {
                  color: mode === 'light' ? '#0f172a' : '#f1f5f9',
                  backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
                },
                '& .MuiInputBase-input': {
                  color: mode === 'light' ? '#0f172a' : '#f1f5f9',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'light' ? '#e2e8f0' : '#334155',
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'light' ? '#64748b' : '#94a3b8',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: mode === 'light' ? '#667eea' : '#8b9eff',
                },
                '& .MuiOutlinedInput-root': {
                  '&:not(.MuiInputBase-multiline)': {
                    height: 40,
                  },
                  '& .MuiInputBase-input': {
                    padding: '10px 12px',
                    height: '100%',
                    boxSizing: 'border-box',
                  },
                },
              },
            },
          },
          MuiInputBase: {
            styleOverrides: {
              root: {
                color: mode === 'light' ? '#0f172a' : '#f1f5f9',
                '&.Mui-disabled': {
                  color: mode === 'light' ? '#94a3b8' : '#64748b',
                },
              },
              input: {
                color: mode === 'light' ? '#0f172a' : '#f1f5f9',
                '&::placeholder': {
                  color: mode === 'light' ? '#94a3b8' : '#64748b',
                  opacity: 1,
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'light' ? '#e2e8f0' : '#334155',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'light' ? '#cbd5e1' : '#475569',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: mode === 'light' ? '#667eea' : '#8b9eff',
                },
                '&:not(.MuiInputBase-multiline)': {
                    height: 40,
                  },
                '& input': {
                  height: '100%',
                  boxSizing: 'border-box',
                },
              },
            },
          },
          MuiTable: {
            styleOverrides: {
              root: {
                color: mode === 'light' ? '#0f172a' : '#f1f5f9',
              },
            },
          },
          MuiTableBody: {
            styleOverrides: {
              root: {
                '& .MuiTableCell-root': {
                  color: mode === 'light' ? '#0f172a' : '#f1f5f9',
                },
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                color: mode === 'light' ? '#0f172a' : '#f1f5f9',
                borderColor: mode === 'light' ? '#e2e8f0' : '#334155',
              },
              head: {
                color: mode === 'light' ? '#475569' : '#94a3b8',
                fontWeight: 600,
                backgroundColor: mode === 'light' ? '#f8fafc' : '#1e293b',
              },
              body: {
                color: mode === 'light' ? '#0f172a' : '#f1f5f9',
              },
            },
          },
          MuiTableRow: {
            styleOverrides: {
              root: {
                '&:hover': {
                  backgroundColor: mode === 'light' ? '#f8fafc' : 'rgba(102, 126, 234, 0.05)',
                },
              },
            },
          },
          MuiTableContainer: {
            styleOverrides: {
              root: {
                backgroundColor: mode === 'light' ? '#ffffff' : '#1e293b',
              },
            },
          },
          MuiTablePagination: {
            styleOverrides: {
              root: {
                color: mode === 'light' ? '#0f172a' : '#f1f5f9',
              },
              selectIcon: {
                color: mode === 'light' ? '#475569' : '#94a3b8',
              },
              actions: {
                color: mode === 'light' ? '#475569' : '#94a3b8',
              },
            },
          },
          MuiChip: {
            variants: [
              {
                props: { label: 'Active' },
                style: {
                  backgroundColor: mode === 'light' ? '#d1fae5' : '#064e3b',
                  color: mode === 'light' ? '#065f46' : '#6ee7b7',
                },
              },
              {
                props: { label: 'Inactive' },
                style: {
                  backgroundColor: mode === 'light' ? '#fee2e2' : '#7f1d1d',
                  color: mode === 'light' ? '#dc2626' : '#fca5a5',
                },
              },
            ],
          },
          MuiButton: {
            styleOverrides: {
              root: {
                minHeight: 40,
                height: 40,
                padding: '6px 16px',
                lineHeight: 1,
                '&.MuiButton-sizeSmall': {
                  minHeight: 36,
                  height: 36,
                },
              },
            },
          },
          MuiInputAdornment: {
            styleOverrides: {
              root: {
                height: 40,
                alignItems: 'center',
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            '.MuiBox-root-margin': {
              marginLeft: '1% !important',
              marginRight: '1% !important',
            },
          }}
        />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
